import { HttpBackend } from '../../Services/Http/HttpBackend';
import { ExploreEntryBuilder } from './ExploreEntryBuilder';
import { ExploreContent, ExploreContentQueryRequest } from './ExploreContent';
import { createExploreCategory } from './ExploreCategory';
import { createRecipe, Recipe } from '../Diet/Recipe/Recipe';
import { v1ParamsToV3Params } from './exploreV3Params';
import { getAllExploreCategoriesV3, resolveCategoryId } from './exploreCategoryTreeV3';

export type KinasticContent = ExploreContent | Recipe;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

// The backend serializes error `status` fields as Spring `HttpStatus` enum names
// (e.g. "NOT_FOUND") rather than numeric codes, so map the ones we branch on.
const HTTP_STATUS_NAME_TO_CODE: Record<string, number> = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
};

function statusOf(value: unknown): number | undefined {
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }

  const status = record.status;
  if (typeof status === 'number') {
    return status;
  }
  if (typeof status === 'string') {
    const parsed = Number(status);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    return HTTP_STATUS_NAME_TO_CODE[status];
  }

  return statusOf(record.response);
}

function isBackendErrorPayload(value: unknown): boolean {
  const status = statusOf(value);
  if (status !== undefined && status >= 400) {
    return true;
  }

  const record = asRecord(value);
  return typeof record?.errorCode === 'string' || typeof record?.errorMessage === 'string';
}

async function getOptionalContent(url: string): Promise<Record<string, unknown> | undefined> {
  const content: unknown = await HttpBackend.get(url).catch((error: unknown) => {
    if (statusOf(error) === 404) {
      return undefined;
    }
    throw error;
  });

  if (isBackendErrorPayload(content)) {
    if (statusOf(content) === 404) {
      return undefined;
    }
    throw content;
  }

  return asRecord(content);
}

/**
 * V3 splits entries, categories and recipes into physically distinct
 * collections, so the old polymorphic `/coach/explore` surface is reconstructed
 * client-side:
 *  - `find`/`count` resolve the requested parent category to a UUID and return
 *    its DIRECT child categories + DIRECT child entries (level-by-level).
 *  - `findOne` resolves the CATEGORY first (a `/<area>/<slug>` URL is a category
 *    identifier by design), then probes the entry endpoint (which also serves
 *    recipes) and the recipe API, and finally the category-by-id endpoint.
 */
export const ExploreContentBuilderApi = {
  async find(request: ExploreContentQueryRequest): Promise<KinasticContent[]> {
    const all = await getAllExploreCategoriesV3();
    const parentId = resolveCategoryId(all, request.categories?.[0]);
    const childCategories = parentId ? all.filter((c) => (c.parentIds ?? []).includes(parentId)) : [];

    const params = v1ParamsToV3Params({ ...request, ...(parentId ? { parentId } : {}) });
    const entries = await HttpBackend.get(`/coach/explore/v3/entries`, params);
    const entryContent = (entries ?? []).map((r: any) => ExploreEntryBuilder.build(r));

    return [...childCategories, ...entryContent];
  },

  async count(request: ExploreContentQueryRequest): Promise<number> {
    const all = await getAllExploreCategoriesV3();
    const parentId = resolveCategoryId(all, request.categories?.[0]);
    const params = v1ParamsToV3Params({ ...request, ...(parentId ? { parentId } : {}) });
    return HttpBackend.get(`/coach/explore/v3/entries/count`, params).then((res) => res ?? 0);
  },

  async findOne(id: string): Promise<KinasticContent> {
    // A `/<area>/<slug>` URL is, by design, almost always a CATEGORY identifier, so
    // resolve the (cached, authoritative) category list FIRST. This is effectively
    // what the V1 single polymorphic endpoint did, and it stops a flaky/over-eager
    // entry or recipe probe from hijacking a category slug — which was rendering real
    // categories as empty recipes ("0 Minuten / 1 Portion").
    const allCategories = await getAllExploreCategoriesV3().catch(() => undefined);
    if (allCategories) {
      const categoryId = resolveCategoryId(allCategories, id);
      const resolvedCategory = categoryId ? allCategories.find((category) => category.id === categoryId) : undefined;
      if (resolvedCategory) {
        return resolvedCategory;
      }
    }
    // Not a known category: try the entry endpoint (which also serves recipes), then
    // the recipe API. Require a real document whose id matches the request — a 404 /
    // empty / error / mismatched body must NOT be accepted as a hit (that false
    // positive is what produced the bogus empty recipe).
    const entry = await getOptionalContent(`/coach/explore/v3/entries/${id}`);
    if (entry && entry.id === id) {
      // The V3 entries endpoint also serves recipes (type: 'recipe'), which are a
      // separate model — build a Recipe instead of coercing the type to 'default'.
      return entry.type === 'recipe' ? createRecipe(entry) : ExploreEntryBuilder.build(entry);
    }
    const recipe = await getOptionalContent(`/coach-program/diet/recipes/${id}`);
    if (recipe && recipe.id === id) {
      return createRecipe(recipe);
    }
    // Last resort: the category-by-id endpoint resolves a slug or id server-side
    // (covers categories outside the cached/paged flat list).
    const category = await HttpBackend.get(`/coach/explore/v3/categories/${id}`);
    if (isBackendErrorPayload(category)) {
      throw category;
    }
    return createExploreCategory(category);
  },
};

export const ExploreContentBuilder = ExploreContentBuilderApi;
